import { Card, Chip, Table, TBody, TD, TH, THead, TR } from '../ui'
import {
  isBeyondCapacity,
  isFull,
  isNearFull,
  isStuck,
  parseDoorHealth,
  usagePct,
  type DoorHealthRow,
} from '../../lib/doorHealth'
import type { EdgeInstance } from '../../api/types'
import { relativeTime, shortDateTime } from '../../lib/format'

const BEYOND_CAPACITY_HINT =
  "Records above the configured capacity are not managed: a revoked person among them keeps door access until the door's record_capacity setting is fixed."

/** Compact used/capacity bar for one door — warn at ≥90% (near full),
 *  danger past 100% (over capacity, which should not happen but the
 *  console renders it rather than hiding it). Unknown values show "—". */
function DoorUsageBar({ row }: { row: DoorHealthRow }) {
  const pct = usagePct(row)
  // Tone reacts to the real (uncapped) percentage — a door past 100% stays
  // danger-colored — but the label and bar width clamp to 0–100 so odd
  // telemetry (a huge capacity, an over-capacity door) never overflows or
  // prints something like "150%".
  const clamped = pct === null ? null : Math.min(100, Math.max(0, pct))
  const tone = pct === null ? 'bg-ostrel-dim' : pct >= 100 ? 'bg-ostrel-danger' : pct >= 90 ? 'bg-ostrel-warn' : 'bg-ostrel-ok'
  return (
    <div className="min-w-[8rem]">
      <div className="flex items-baseline justify-between gap-2">
        <span className="tnum text-xs text-ostrel-ink">
          {row.used === null || row.capacity === null ? '—' : `${row.used} / ${row.capacity}`}
        </span>
        {clamped !== null && <span className="tnum text-[10px] text-ostrel-ink-muted">{Math.round(clamped)}%</span>}
      </div>
      <div className="mt-1 h-1.5 w-full rounded-sm bg-ostrel-sand">
        {clamped !== null && <div className={`h-1.5 rounded-sm ${tone}`} style={{ width: `${clamped}%` }} />}
      </div>
    </div>
  )
}

function DoorBadges({ row }: { row: DoorHealthRow }) {
  const chips = []
  if (isFull(row)) chips.push(<Chip key="full" tone="danger" label={`Full (${row.unplaced})`} />)
  if (isStuck(row)) chips.push(<Chip key="stuck" tone="danger" label="Stuck" />)
  if (isNearFull(row)) chips.push(<Chip key="near-full" tone="warn" label="Near full" />)
  if (isBeyondCapacity(row)) {
    chips.push(
      <Chip
        key="beyond-capacity"
        tone="danger"
        label={`Beyond capacity: ${row.beyondCapacity}`}
        title={BEYOND_CAPACITY_HINT}
      />,
    )
  }
  if (chips.length === 0) return <span className="text-ostrel-ink-muted">—</span>
  return <span className="flex flex-wrap gap-1">{chips}</span>
}

function InstanceDoorTable({ rows }: { rows: DoorHealthRow[] }) {
  if (rows.length === 0) {
    return <p className="text-[11px] text-ostrel-ink-muted">No doors configured.</p>
  }
  return (
    <Table>
      <THead>
        <TR>
          <TH>Door</TH>
          <TH>Usage</TH>
          <TH>Pending ops</TH>
          <TH>Status</TH>
        </TR>
      </THead>
      <TBody>
        {rows.map((row) => (
          <TR key={row.doorId}>
            <TD>
              {row.name ? (
                <>
                  <span className="block text-sm font-medium text-ostrel-ink">{row.name}</span>
                  <span className="tnum block font-mono text-[11px] text-ostrel-ink-muted">{row.doorId}</span>
                </>
              ) : (
                <span className="tnum font-mono text-xs text-ostrel-ink">{row.doorId}</span>
              )}
            </TD>
            <TD>
              <DoorUsageBar row={row} />
            </TD>
            <TD className="tnum text-xs text-ostrel-ink">{row.pendingOps ?? '—'}</TD>
            <TD>
              <DoorBadges row={row} />
            </TD>
          </TR>
        ))}
      </TBody>
    </Table>
  )
}

/**
 * Per-door health for this gateway's edge instances (axema DCIP worker
 * `status` frames — `EdgeInstance.reported.doors`). Renders nothing when no
 * instance carries `doors` (old agents, or non-DCIP instances): the whole
 * card disappears rather than showing an empty shell.
 */
export function DoorHealthCard({
  instances,
  connected,
  now,
}: {
  instances: EdgeInstance[]
  /** The gateway's live connection state. `false` = the numbers are the
   *  last known values (agents report on change, not periodically, so age
   *  alone never marks them stale). */
  connected?: boolean
  now?: number
}) {
  const withDoors = instances
    .map((inst) => ({ inst, rows: parseDoorHealth(inst.reported, inst.params) }))
    .filter((x): x is { inst: EdgeInstance; rows: DoorHealthRow[] } => x.rows !== null)

  if (withDoors.length === 0) return null

  return (
    <Card title="Door health">
      {connected === false && (
        <p className="mb-3 rounded-sm border border-ostrel-warn/40 bg-ostrel-warn/10 px-3 py-2 text-xs text-ostrel-warn">
          Appliance disconnected: showing the last known values.
        </p>
      )}
      <div className={`flex flex-col gap-5${connected === false ? ' opacity-60' : ''}`}>
        {withDoors.map(({ inst, rows }) => (
          <div key={inst.unique_id}>
            {withDoors.length > 1 && (
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
                {inst.name}
              </p>
            )}
            <InstanceDoorTable rows={rows} />
            <p className="mt-1 text-[10px] text-ostrel-ink-muted">
              {inst.reported_at ? (
                <span title={shortDateTime(inst.reported_at)}>Reported {relativeTime(inst.reported_at, now)}</span>
              ) : (
                'Report time unknown'
              )}
            </p>
          </div>
        ))}
      </div>
    </Card>
  )
}
